import React from 'react';

import { useGetCustomerSupplierMappings } from '@ar/hooks/queries/CustomerQueries.hook';
import { SIGN_UP_SUPPLIER_ID_KEY } from '@ar/pages/SignUpPage/CustomerSignUpPage';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import { Banner, EBannerType, Loading, mergeClassNames, Modal, Typography } from '@notch-ordering/ui-components';
import { getGpoId } from '@v2/utils/GPOUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ActivePaymentMethods } from '@/utils/constants';
import { PaymentMethodType } from '@/ar/network/AccountsReceivable.network';
import { useGetSupplier } from '@/ar/hooks/queries/SupplierQueries.hook';
import { useGetAdyenPaymentMethodType } from '@/ar/hooks/queries/PaymentReceiverQueries.hook';
import { drawerInsideClassName } from '@/ar/components/CustomerDetails/CustomerGeneralPopup/CustomerGeneralPopup';
import { CreateAdyenCardPaymentMethod } from './CreateAdyenCardPaymentMethod/CreateAdyenCardPaymentMethod';
import { CreatePaymentMethodSteps, isACHEnabledAsPaymentType, isCreditCardEnabledAsPaymentType, isEFTEnabledAsPaymentType } from './CreateAdyenPaymentMethodConstants';
import { CreateAdyenPaymentMethodSelectionStep } from './CreateAdyenPaymentMethodSelectionStep/CreateAdyenPaymentMethodSelectionStep';
import { useCreatePaymentMethodStore } from '@/ar/stores/CreatePaymentMethodStore';
import { CreateBankAccount } from './CreateBankAccount/CreateBankAccount';
import './CreatePaymentMethodModal.scss';
import { BankAccountType } from './CreateBankAccount/CreateBankAccountConstants';
import { AcceptMandateForm } from '@/ar/components/MandatePage/AcceptMandateForm/AcceptMandateForm';
import { useGetMandate } from '@/ar/hooks/queries/MandateQueries.hook';

export interface PaymentMethodModalProps {
    isOpen?: boolean,
    handleClose?: () => void,
    subTitle?: string,
    customerID?: string,
}

export const getModalTitlePerStep = (currentStep: CreatePaymentMethodSteps, subTitle, paymentMethodType?: BankAccountType): React.ReactNode => {
    const isACH = paymentMethodType === PaymentMethodType.US_BANK_ACCOUNT;
    switch (currentStep) {
        case CreatePaymentMethodSteps.SELECT_PAYMENT_METHOD:
            return <div className="flex flex-col gap-2">
                <Typography variant="2XL" className="font-bold m-0">Add a payment method</Typography>
            </div>;
        case CreatePaymentMethodSteps.ADD_CREDIT_CARD:
            return (
                <>
                    <Typography className="m-0 pr-10 mb-1" weight="font-semibold" variant="LG-2">Create new payment method</Typography>
                    {Boolean(subTitle) && <Typography variant="LG-1" weight="font-regular" className="text-gray-600">{subTitle}</Typography>}
                </>
            );

        case CreatePaymentMethodSteps.ADD_BANK_ACCOUNT:
            return <div className="flex flex-row gap-2">
                <Typography className="m-0 pr-10" weight="font-medium" variant="LG-2" desktopSize="lg:text-5">
                    {isACH ? 'Add a US bank account' : 'Add a Canadian bank account'}
                </Typography>
            </div>;

        case CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE:
            return <div className="flex flex-row gap-2">
                <Typography className="m-0 pr-10" weight="font-medium" variant="LG-2" desktopSize="lg:text-5">
                    Authorization
                </Typography>
            </div>;
        case CreatePaymentMethodSteps.BANK_ACCOUNT_ACCEPT_MANDATE:
            return <div className="flex flex-row gap-2">
                <Typography className="m-0 pr-10" weight="font-medium" variant="LG-2" desktopSize="lg:text-5">
                    Accept your agreement to enable unlimited online Bank Payments with no fees
                </Typography>
            </div>;
        default:
            return <div className="flex flex-col gap-2">
                <Typography variant="2XL" className="font-bold m-0">Add a payment method</Typography>
            </div>;
    }
};

const getModalHeight = (currentStep: CreatePaymentMethodSteps) => {
    switch (currentStep) {
        case CreatePaymentMethodSteps.SELECT_PAYMENT_METHOD:
            return 'lg:min-h-[370px]';
        case CreatePaymentMethodSteps.ADD_CREDIT_CARD:
            return 'lg:min-h-[400px]';
        case CreatePaymentMethodSteps.ADD_BANK_ACCOUNT:
            return 'lg:min-h-[705px]';
        case CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE:
            return 'lg:min-h-[530px]';
        case CreatePaymentMethodSteps.BANK_ACCOUNT_ACCEPT_MANDATE:
            return 'lg:min-h-[570px]';
        default:
            return 'lg:min-h-[430px]';
    }
};

export const CreateAdyenPaymentMethodModal : React.FC<PaymentMethodModalProps> = ({ isOpen, handleClose, subTitle, customerID } : PaymentMethodModalProps) => {
    const { supplierLoginData, supplier } = useSupplierStore();
    const {
        isCreatePaymentMethodModalOpen,
        setCreatePaymentMethodModalOpen,
        currentStep,
        setCurrentStep,
        paymentMethodType,
        setPaymentMethodType,
        resetPaymentMethodState,
        mandateId
    } = useCreatePaymentMethodStore();
    const gpoId = getGpoId(supplierLoginData);
    const { data } = useGetCustomerSupplierMappings({
        customerID: supplierLoginData.customer_id
    });
    const flags = useFlags();

    const isCustomer = supplierLoginData?.isCustomer;

    const { data: mandate, isLoading: isMandateLoading, isError: isMandateError } = useGetMandate({ mandateID: mandateId as string });

    // id is stored in local storage when the customer signs up if not then use the first supplier id from the supplier mappings list (first check if we are a supplier and a customer)
    const supplierID = supplierLoginData.supplier_id ?? window.localStorage.getItem(SIGN_UP_SUPPLIER_ID_KEY) ?? data?.suppliers[0]?.id;

    useGetSupplier({
        supplierID,
        customerId: supplierLoginData?.customer_id,
        gpoId
    });

    const { data: adyenPaymentMethodTypeResponse, isLoading: isAdyenPaymentMethodTypeLoading } = useGetAdyenPaymentMethodType({
        paymentReceiverId: supplier?.payment_receiver_id,
        supplierId: supplierID,
        customerId: customerID
    });

    const onClose = () => {
        resetPaymentMethodState();
        handleClose();
    };

    React.useEffect(() => {
        if (isOpen) {
            setCreatePaymentMethodModalOpen(true);
        }
    }, [isOpen]);

    const hasNoPaymentMethodsEnabled = !supplier?.allow_customer_pay_with_bank_debits && !supplier?.allow_customer_pay_with_cards;
    const isACHEnabled = flags?.arPaymentProvider?.us_bank_account === ActivePaymentMethods.adyen && isACHEnabledAsPaymentType(adyenPaymentMethodTypeResponse?.paymentTypes, isCustomer);
    const isEFTEnabled = flags?.arPaymentProvider?.acss_debit === ActivePaymentMethods.adyen && isEFTEnabledAsPaymentType(adyenPaymentMethodTypeResponse?.paymentTypes, isCustomer);
    const showCreditCardButton = !!supplier?.allow_customer_pay_with_cards && isCreditCardEnabledAsPaymentType(adyenPaymentMethodTypeResponse?.paymentTypes, isCustomer);
    const allowSupplierToManageCards = !!supplier?.allow_supplier_manage_customer_cards;
    const showSupplierCreditCardButton = !isCustomer && allowSupplierToManageCards
        && isCreditCardEnabledAsPaymentType(adyenPaymentMethodTypeResponse?.paymentTypes, isCustomer);
    const shouldShowCreditCardButton = showCreditCardButton || showSupplierCreditCardButton;
    const showACHButton = !!supplier?.allow_customer_pay_with_bank_debits && isACHEnabled;
    const showEFTButton = !!supplier?.allow_customer_pay_with_bank_debits && isEFTEnabled;
    const isCurrentStepInBankAccountFlow = [CreatePaymentMethodSteps.ADD_BANK_ACCOUNT, CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE].includes(currentStep) && paymentMethodType;

    if (!isOpen) {
        return null;
    }

    return <Modal
        className={drawerInsideClassName}
        isOpen={isCreatePaymentMethodModalOpen}
        close={onClose}
        onClose={onClose}
        titleSeparatorDesktop={true}
        desktopModalWidth={mergeClassNames('lg:w-[560px] transition-all', getModalHeight(currentStep))}
        title={getModalTitlePerStep(currentStep, subTitle, paymentMethodType)}>
        {isAdyenPaymentMethodTypeLoading && <Loading isDark className="my-8" />}
        {!isAdyenPaymentMethodTypeLoading && (
            <>
                {currentStep === CreatePaymentMethodSteps.SELECT_PAYMENT_METHOD
                    && <CreateAdyenPaymentMethodSelectionStep
                        hasNoPaymentMethodsEnabled={hasNoPaymentMethodsEnabled}
                        onCardSelect={() => {
                            setCurrentStep(CreatePaymentMethodSteps.ADD_CREDIT_CARD);
                            setPaymentMethodType(null);
                        }}
                        onACHSelect={() => {
                            setCurrentStep(CreatePaymentMethodSteps.ADD_BANK_ACCOUNT);
                            setPaymentMethodType(PaymentMethodType.US_BANK_ACCOUNT);
                        }}
                        onEFTSelect={() => {
                            setCurrentStep(CreatePaymentMethodSteps.ADD_BANK_ACCOUNT);
                            setPaymentMethodType(PaymentMethodType.ACSS_DEBIT);
                        }}
                        showACHButton={showACHButton}
                        showEFTButton={showEFTButton}
                        showCreditCardButton={shouldShowCreditCardButton}/>
                }
                {/* CREDIT CARD FLOW handle one step in the main component */}
                {currentStep === CreatePaymentMethodSteps.ADD_CREDIT_CARD && <CreateAdyenCardPaymentMethod
                    isModal={false}
                    handleClose={onClose}
                    subTitle={subTitle}
                    supplierID={supplierID}
                    customerID={customerID}/>}

                {/* BANK ACCOUNT FLOW handle two steps in the main component */}
                {(isCurrentStepInBankAccountFlow) && <CreateBankAccount
                    customerID={customerID}
                    handleClose={onClose}
                    supplierID={supplierID}/>}
                {currentStep === CreatePaymentMethodSteps.BANK_ACCOUNT_ACCEPT_MANDATE
                && <>
                    {isMandateLoading && <Loading isDark className="my-8" />}
                    {!isMandateLoading && mandate && <AcceptMandateForm mandate={mandate}
                        wrapperClassName="px-8 pb-0"
                        showTitle={false}
                        onCancel={onClose}
                        handleSuccess={onClose} />}
                    {isMandateError && <div className="px-8 my-8"><Banner alertType={EBannerType.ERROR}
                        className="px-8"
                        title="Error loading mandate "
                        body="Please try again later" /></div>}
                </>
                }

            </>
        )}
    </Modal>;
};
